import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import {
  AddressFragment,
  CouponDurationTypeEnum,
  RateGroupKindEnum,
} from '@graphql/platform';

import {
  challengerPromoMonthlySubtext,
  challengerPromoPickupSubtext,
  challengerPromoSummaryLabel,
  couponDiscountAmount,
  isAppointmentCoupon,
  isStorageCoupon,
} from '@root/components/checkout/cart/challenger_promo';
import { useReservationSummaryProps } from '@root/components/checkout/cart/use_reservation_summary_props';
import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { StorageCheckoutStep } from '@root/components/checkout/types';
import { PLANS } from '@root/constants/pricing';
import { Commitment } from '@root/resources/types/commitment';

import { Address } from '@shared/formatters/address';

import { formatCurrency } from '@utils/formatters';
import { PROTECTION_PLANS } from '@utils/protection_plan';

import { DisposalSummaryRow } from './disposal_summary_row';
import { useChallengerPricingRows } from './hooks/use_challenger_pricing_rows';
import { useCloseMobileDrawerOnOutsideInteraction } from './hooks/use_close_mobile_drawer_on_outside_interaction';
import { useKeyboardInset } from './hooks/use_keyboard_inset';
import { useModalFocus } from './hooks/use_modal_focus';
import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { CHALLENGER as T } from './tokens';

/** Matches plan selector answers under the size line (Figma 29:3865). */
const COMMITMENT_LABEL: Record<Commitment, string> = {
  [RateGroupKindEnum.Mover]: 'Month to month',
  [RateGroupKindEnum.Flexer]: '4-month minimum',
  [RateGroupKindEnum.Saver]: '8-month minimum',
};

/** Collapsed bar — whole surface opens the details sheet (same as plan sticky). */
const Shell = styled.div<{ $keyboardInset: number }>`
  display: none;

  ${MOBILE} {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    flex-direction: column;
    align-items: center;
    background: ${T.color.stickySurface};
    border: 1px solid ${T.color.borderDefault};
    border-bottom: none;
    border-radius: ${T.radius.lg} ${T.radius.lg} 0 0;
    padding-top: ${T.space[8]};
    padding-bottom: env(safe-area-inset-bottom, 0);
    font-family: ${T.font.sans};
    box-sizing: border-box;
    cursor: pointer;
    transform: ${({ $keyboardInset }) =>
      $keyboardInset ? `translateY(${$keyboardInset}px)` : 'none'};
    pointer-events: ${({ $keyboardInset }) =>
      $keyboardInset ? 'none' : 'auto'};
  }
`;

const Handle = styled.div`
  width: 40px;
  height: 6px;
  border-radius: ${T.radius.pill};
  background: #c8d1d3;
  flex-shrink: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${T.space[16]};
  width: 100%;
  padding: ${T.space[8]} ${T.space[12]} ${T.space[16]} 2px;
  box-sizing: border-box;
`;

const Left = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 ${T.space[16]};
`;

const PriceLine = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  padding: 2px 0;
`;

const Price = styled.span`
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 22px;
  color: ${T.color.textSecondary};
`;

const PerMonth = styled.span`
  font-size: 13px;
  font-weight: ${FontWeight.Medium};
  line-height: 18px;
  color: ${T.color.textPrimary};
`;

/** Visual affordance only — whole Shell opens the details sheet. */
const DetailsLink = styled.span`
  display: inline-block;
  padding: 2px;
  align-self: flex-start;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Semibold};
  line-height: 14px;
  color: ${T.color.linkBrand};
`;

const Backdrop = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  position: fixed;
  inset: 0;
  z-index: 29;
  background: ${T.color.overlay};
  cursor: pointer;

  ${DESKTOP} {
    display: none;
  }
`;

/** Figma 913:43587 — expanded reservation details sheet. */
const Sheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 31;
  max-height: 90vh;
  overflow: auto;
  background: ${T.color.stickySurface};
  border: 1px solid ${T.color.borderDefault};
  border-bottom: none;
  border-radius: ${T.radius.lg} ${T.radius.lg} 0 0;
  padding: ${T.space[8]} 0
    calc(${T.space[16]} + env(safe-area-inset-bottom, 0px));
  font-family: ${T.font.sans};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${DESKTOP} {
    display: none;
  }
`;

const SheetInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  padding: ${T.space[16]};
  padding-bottom: ${T.space[16]};
  box-sizing: border-box;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  padding-bottom: ${T.space[24]};
  border-bottom: 1px solid ${T.color.borderDefault};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${T.space[8]};
  padding: 0 0 ${T.space[16]} 12px;
  border-bottom: 1px solid ${T.color.borderDefault};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 22px;
  color: ${T.color.textSecondary};
`;

const ChangeButton = styled.button`
  appearance: none;
  border: 1px solid ${T.color.borderDefault};
  background: transparent;
  border-radius: ${T.radius.sm};
  padding: ${T.space[8]} ${T.space[12]};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 14px;
  color: ${T.color.textPrimary};
`;

const ArrivalDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[12]};
  width: 100%;
`;

const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 12px;
`;

const DetailLabel = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
  padding: 2px 0;
`;

const DetailValue = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Medium};
  line-height: 18px;
  color: ${T.color.textSecondary};
`;

const BookingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
`;

const BookingHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 0 ${T.space[16]} 12px;
  border-bottom: 1px solid ${T.color.borderDefault};
`;

const BookingPlanCaption = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 20px;
  color: ${T.color.textSecondary};
`;

const BookingHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${T.space[8]};
  width: 100%;
`;

const CostStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  padding: 0 12px;
`;

const CostGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CostLabel = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
  padding: 2px 0;
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${T.space[8]};
  font-size: 13px;
  font-weight: ${FontWeight.Medium};
  line-height: 18px;
  color: ${T.color.textSecondary};
`;

const PromoLine = styled(Line)`
  color: ${T.color.textBrandDark};
  font-weight: ${FontWeight.Regular};
  padding: 2px 0;
`;

const Total = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  padding: ${T.space[16]};
  box-sizing: border-box;
  border-radius: ${T.radius.md};
  background: ${T.color.timelineSurface};
  border: 1px solid rgba(198, 170, 119, 0.4);
`;

const TimelineRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${T.space[12]};
  width: 100%;
`;

/**
 * Figma 707:5568 — 8px gold dots with 1px connectors between steps.
 */
const BulletCol = styled.div<{ $connect?: boolean }>`
  position: relative;
  width: 8px;
  flex-shrink: 0;
  align-self: stretch;
  padding-top: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ $connect }) =>
    $connect
      ? `
    &::after {
      content: '';
      position: absolute;
      top: 8px;
      bottom: calc(-${T.space[16]} - 8px);
      left: 50%;
      width: 1px;
      margin-left: -0.5px;
      border-radius: 999px;
      background: rgba(198, 170, 119, 0.4);
    }
  `
      : ''}
`;

const Bullet = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${T.color.iconBrandSecondary};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const TimelineBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const TimelineCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const TimelineTitle = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
`;

const TimelineSub = styled.div`
  font-size: 10px;
  font-weight: ${FontWeight.Medium};
  line-height: 14px;
  color: ${T.color.textSecondary};
`;

const TimelineAmount = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
  white-space: nowrap;
`;

type Props = {
  onEdit: (step: StorageCheckoutStep) => void;
};

/** Mobile confirm sticky + full reservation sheet (Figma 913:43587). */
export const MobileConfirmSticky: React.FC<Props> = ({ onEdit }) => {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const keyboardInset = useKeyboardInset();
  useModalFocus(open, sheetRef);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useCloseMobileDrawerOnOutsideInteraction(open, sheetRef, () =>
    setOpen(false),
  );

  const {
    flowState: {
      values: {
        planSize,
        commitment,
        bundleKind,
        disposalSelected,
        dateScheduled,
        datePreferred,
        address,
        coupon,
        protectionPlan,
      },
    },
  } = useStorageCheckoutContext();
  const summary = useReservationSummaryProps('challenger');

  const protectionCoverage = protectionPlan
    ? PROTECTION_PLANS[protectionPlan].coverage
    : undefined;

  const tax = (summary.storagePlanTax || 0) + (summary.protectionPlanTax || 0);
  const fullMonthlyTotal = summary.summaryLoaded
    ? summary.monthlyTotalAmount + tax
    : 0;
  const monthlyBaseAmount = summary.summaryLoaded
    ? summary.storagePlanAmount + summary.protectionPlanAmount
    : 0;

  const {
    appointmentPromoDiscount,
    disposalAmount,
    gasFeeAmount: ecoFeeAmount,
    packingLabel,
    packingPriceDisplay,
    pickupTotal,
    pickupTotalDisplay,
    scheduledArrivalAmount,
    showPackingLine,
    showPickupFees,
  } = useChallengerPricingRows({
    bundleKind,
    coupon,
    dateScheduled,
    disposalSelected,
  });

  // Figma 913:43587 — storage promos under Storage plan (before Tax);
  // appointment promos under Pick-up fees (below Gas fee).
  const storagePromoDiscount =
    coupon && isStorageCoupon(coupon) && summary.summaryLoaded
      ? couponDiscountAmount(coupon, monthlyBaseAmount)
      : 0;

  const storageSectionTotal = Math.max(
    0,
    fullMonthlyTotal - storagePromoDiscount,
  );
  const ongoingMonthlyTotal =
    coupon && isStorageCoupon(coupon)
      ? coupon.duration === CouponDurationTypeEnum.Once
        ? fullMonthlyTotal
        : storageSectionTotal
      : fullMonthlyTotal;
  const dueOnPickup =
    pickupTotal != null ? storageSectionTotal + pickupTotal : undefined;

  const pickupDateLabel = datePreferred
    ? datePreferred.toFormat('cccc, MMM d, yyyy')
    : 'Select a pickup date';
  const arrivalWindowLabel = dateScheduled?.formatted
    ? `Between ${dateScheduled.formatted}`
    : undefined;

  const pickupDayLabel = datePreferred
    ? datePreferred.toFormat('MMMM d, yyyy')
    : undefined;

  const monthStartLabel = datePreferred
    ? `Starting ${datePreferred.plus({ months: 1 }).toLocaleString({
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })}`
    : undefined;

  // Appointment promos annotate pick-up day; storage promos use monthly subtext.
  const pickupSubtext =
    coupon && isAppointmentCoupon(coupon)
      ? challengerPromoPickupSubtext(pickupDayLabel)
      : pickupDayLabel;
  const monthlySubtext = coupon
    ? challengerPromoMonthlySubtext(coupon, fullMonthlyTotal, monthStartLabel)
    : monthStartLabel;

  const closeAndEdit = (step: StorageCheckoutStep) => {
    setOpen(false);
    onEdit(step);
  };

  return (
    <>
      {open && (
        <>
          <Backdrop
            type="button"
            aria-label="Close details"
            onClick={() => setOpen(false)}
          />
          <Sheet
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="Reservation details"
          >
            <Handle aria-hidden />
            <SheetInner>
              <Section>
                <SectionHeader>
                  <SectionTitle>Pickup details</SectionTitle>
                  <ChangeButton
                    type="button"
                    onClick={() =>
                      closeAndEdit(StorageCheckoutStep.AddressAndLeadInfo)
                    }
                  >
                    Change
                  </ChangeButton>
                </SectionHeader>
                <ArrivalDetails>
                  <DetailBlock>
                    <DetailLabel>Address</DetailLabel>
                    <DetailValue>
                      <Address
                        inline
                        address={(address as AddressFragment) ?? {}}
                      />
                    </DetailValue>
                  </DetailBlock>
                  <DetailBlock>
                    <DetailLabel>Date</DetailLabel>
                    <DetailValue>{pickupDateLabel}</DetailValue>
                  </DetailBlock>
                  {arrivalWindowLabel && (
                    <DetailBlock>
                      <DetailLabel>Arrival window</DetailLabel>
                      <DetailValue>{arrivalWindowLabel}</DetailValue>
                    </DetailBlock>
                  )}
                </ArrivalDetails>
              </Section>

              <BookingSection>
                <BookingHeader>
                  <BookingHeaderRow>
                    <SectionTitle>Booking summary</SectionTitle>
                    <ChangeButton
                      type="button"
                      onClick={() => closeAndEdit(StorageCheckoutStep.PlanSize)}
                    >
                      Change
                    </ChangeButton>
                  </BookingHeaderRow>
                  {planSize && (
                    <BookingPlanCaption>
                      {PLANS[planSize].label} - {PLANS[planSize].description}
                    </BookingPlanCaption>
                  )}
                </BookingHeader>

                <CostStack>
                  <CostGroup>
                    <CostLabel>Monthly storage plan</CostLabel>
                    {planSize ? (
                      <Line>
                        <span>
                          {commitment
                            ? COMMITMENT_LABEL[commitment]
                            : 'Storage plan'}
                        </span>
                        <span>
                          {summary.summaryLoaded
                            ? formatCurrency(summary.storagePlanAmount)
                            : '—'}
                        </span>
                      </Line>
                    ) : (
                      <Line>
                        <span>Storage plan</span>
                        <span>—</span>
                      </Line>
                    )}
                    {summary.protectionPlanAmount > 0 && protectionCoverage && (
                      <Line>
                        <span>{protectionCoverage} of protection</span>
                        <span>
                          {formatCurrency(summary.protectionPlanAmount)}
                        </span>
                      </Line>
                    )}
                    {coupon && storagePromoDiscount > 0 && (
                      <PromoLine>
                        <span>{challengerPromoSummaryLabel(coupon)}</span>
                        <span>-{formatCurrency(storagePromoDiscount)}</span>
                      </PromoLine>
                    )}
                    <Line>
                      <span>Tax</span>
                      <span>
                        {summary.summaryLoaded ? formatCurrency(tax) : '—'}
                      </span>
                    </Line>
                    <Total>
                      {summary.summaryLoaded
                        ? formatCurrency(storageSectionTotal)
                        : '—'}
                    </Total>
                  </CostGroup>

                  {showPickupFees && (
                    <CostGroup>
                      <CostLabel>Your initial pickup includes</CostLabel>
                      {showPackingLine && packingLabel && (
                        <Line>
                          <span>{packingLabel}</span>
                          <span>{packingPriceDisplay}</span>
                        </Line>
                      )}
                      {scheduledArrivalAmount > 0 && (
                        <Line>
                          <span>Scheduled arrival</span>
                          <span>{formatCurrency(scheduledArrivalAmount)}</span>
                        </Line>
                      )}
                      {disposalSelected && (
                        <DisposalSummaryRow amount={disposalAmount} />
                      )}
                      {ecoFeeAmount > 0 && (
                        <Line>
                          <span>Gas fee</span>
                          <span>{formatCurrency(ecoFeeAmount)}</span>
                        </Line>
                      )}
                      {coupon && appointmentPromoDiscount > 0 && (
                        <PromoLine>
                          <span>{challengerPromoSummaryLabel(coupon)}</span>
                          <span>
                            -{formatCurrency(appointmentPromoDiscount)}
                          </span>
                        </PromoLine>
                      )}
                      <Total>{pickupTotalDisplay}</Total>
                    </CostGroup>
                  )}
                </CostStack>
              </BookingSection>

              <Timeline>
                <TimelineRow>
                  <BulletCol $connect aria-hidden>
                    <Bullet />
                  </BulletCol>
                  <TimelineBody>
                    <TimelineCopy>
                      <TimelineTitle>Total due now</TimelineTitle>
                    </TimelineCopy>
                    <TimelineAmount>$0.00</TimelineAmount>
                  </TimelineBody>
                </TimelineRow>
                <TimelineRow>
                  <BulletCol $connect aria-hidden>
                    <Bullet />
                  </BulletCol>
                  <TimelineBody>
                    <TimelineCopy>
                      <TimelineTitle>Total due on pick-up day</TimelineTitle>
                      {pickupSubtext && (
                        <TimelineSub>{pickupSubtext}</TimelineSub>
                      )}
                    </TimelineCopy>
                    <TimelineAmount>
                      {summary.summaryLoaded && dueOnPickup != null
                        ? formatCurrency(dueOnPickup)
                        : '—'}
                    </TimelineAmount>
                  </TimelineBody>
                </TimelineRow>
                <TimelineRow>
                  <BulletCol aria-hidden>
                    <Bullet />
                  </BulletCol>
                  <TimelineBody>
                    <TimelineCopy>
                      <TimelineTitle>Total due each month</TimelineTitle>
                      {monthlySubtext && (
                        <TimelineSub>{monthlySubtext}</TimelineSub>
                      )}
                    </TimelineCopy>
                    <TimelineAmount>
                      {summary.summaryLoaded
                        ? formatCurrency(ongoingMonthlyTotal)
                        : '—'}
                    </TimelineAmount>
                  </TimelineBody>
                </TimelineRow>
              </Timeline>
            </SheetInner>
          </Sheet>
        </>
      )}
      {!open && (
        <Shell
          $keyboardInset={keyboardInset}
          role="button"
          tabIndex={keyboardInset ? -1 : 0}
          aria-hidden={keyboardInset ? true : undefined}
          aria-expanded={false}
          aria-label="Show reservation details"
          onClick={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen(true);
            }
          }}
        >
          <Handle aria-hidden />
          <Row>
            <Left>
              <PriceLine>
                <Price>
                  {summary.summaryLoaded
                    ? formatCurrency(ongoingMonthlyTotal)
                    : '—'}
                </Price>
                <PerMonth>per month</PerMonth>
              </PriceLine>
              <DetailsLink>Show reservation details</DetailsLink>
            </Left>
          </Row>
        </Shell>
      )}
    </>
  );
};
